"use client"

import { motion } from "framer-motion"
import Image from "next/image"
import React, { useContext, useEffect } from "react"

import CloseIcon from "../../../../../public/icons/modal-close-any-color.svg"
import AppContext from "../../AppContext"

import HoverImage from "./HoverImage"

type PartnerModalProps = {
  modalOpen: boolean
}

type LogoSectionProps = {
  title: string
  logosCollection: any
  isFirst?: boolean
  onClose?: () => void
}

const ModalBackdrop: React.FC = () => {
  const context = useContext(AppContext)
  const closeModal = () => {
    context.setImpactReportPartnersModal(false)
  }

  return (
    <motion.div
      className="fixed inset-0 w-full h-screen z-[75] bg-gray-90/80"
      onClick={() => {
        closeModal()
      }}
    />
  )
}

// Create a logo item component using HoverImage
const LogoItem = ({ logo }: { logo: any }) => {
  if (!logo.colorLogo || !logo.blackLogo) return null

  const defaultImage = (
    <div className="flex items-center justify-center w-full h-full">
      <Image
        src={logo.blackLogo.url}
        width={150}
        height={100}
        alt=""
        className="transition-opacity duration-300 object-contain max-w-full"
        style={{ maxHeight: "85px" }}
      />
    </div>
  )

  const hoverImage = (
    <div className="flex items-center justify-center w-full h-full absolute inset-0">
      <Image
        src={logo.colorLogo.url}
        width={150}
        height={100}
        alt=""
        className="transition-opacity duration-300 opacity-0 group-hover:opacity-100 object-contain max-w-full"
        style={{ maxHeight: "85px" }}
      />
    </div>
  )

  return (
    <div className="h-20 flex items-center justify-center px-6 py-6">
      <HoverImage defaultImage={defaultImage} hoverImage={hoverImage} />
    </div>
  )
}

const LogoSection: React.FC<LogoSectionProps> = ({
  title,
  logosCollection,
  isFirst = false,
  onClose,
}) => (
  <div className={isFirst ? "mb-12" : "mb-12 last:mb-0"}>
    <div className="flex items-center justify-between mb-8">
      <h2 className="text-gray-90 heading2 text-2xl font-medium">{title}</h2>
      {isFirst && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="py-2 pl-2"
          aria-label="Close modal"
        >
          <CloseIcon className="w-6 h-6 cursor-pointer text-gray-70" />
        </button>
      )}
    </div>
    <div className="grid grid-cols-2 gap-x-8 gap-y-12 mx-4">
      {logosCollection?.items?.map((logo: any, index: number) => (
        <LogoItem key={`logo-${logo?.blackLogo?.url || index}`} logo={logo} />
      ))}
    </div>
  </div>
)

const PartnerModal: React.FC<PartnerModalProps> = ({ modalOpen }) => {
  const context = useContext(AppContext)
  const closeModal = () => {
    context.setImpactReportPartnersModal(false)
  }

  const contentData = context.impactReportPartnersModal.contentData || {}
  const logoModalCollection = (contentData as any).logoModalCollection || {
    items: [],
  }

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add("body-modal-open")
    } else {
      document.body.classList.remove("body-modal-open")
    }
    // Cleanup function to ensure class is removed if component is unmounted
    return () => {
      document.body.classList.remove("body-modal-open")
    }
  }, [modalOpen])

  if (!logoModalCollection.items || logoModalCollection.items.length === 0)
    return null

  return (
    modalOpen && (
      <>
        <ModalBackdrop />
        <div className="rounded-lg bg-gray-2.5 fixed z-[75] left-4 right-4 top-4 bottom-4 md:max-w-[480px] md:w-[480px] md:mx-auto md:h-auto md:top-[calc(50vh-294px)] md:bottom-auto">
          <div className="flex flex-col overflow-auto max-h-[588px] min-h-[300px] p-8">
            {logoModalCollection.items.map((section: any, idx: number) => (
              <LogoSection
                key={`section-${section.title || idx}`}
                title={section.title}
                logosCollection={section.logosCollection}
                isFirst={idx === 0}
                onClose={idx === 0 ? closeModal : undefined}
              />
            ))}
          </div>
        </div>
      </>
    )
  )
}

export default PartnerModal
